import { useMemo } from 'react';

import { attackSurfaceOptions } from '@/app/constant';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { BOOLEAN_FILTERS } from '@/sections/vulnerabilities/constants/filters';
import { useAuth } from '@/state/auth';
import { RiskPriority, RiskSeverity, SeverityDef } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { getAvailableStatusOptions } from '@/utils/availableStatuses.util';
import { getShortName } from '@/utils/integrationDisplay.util';
import { transformSourceFilterOptions } from '@/utils/originDisplay.util';

// Define GroupOption locally to avoid cross-imports from component files
type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

// Add a type declaration for window.CHARIOT_USER

declare global {
  interface Window {
    CHARIOT_USER?: { isPraetorianUser?: boolean };
  }
}

// Export the vulnerability field names for use in other components
export const VULNERABILITY_FIELD_NAMES = {
  name: 'name',
  severity: 'severity',
  priority: 'priority',
  status: 'status',
  origin: 'origin',
  tags: 'tags',
  attackSurface: 'attackSurface',
  cve: 'cve',
  cvss: 'cvss',
  epss: 'epss',
  kev: 'kev',
} as const;

export type VulnerabilityFieldName =
  (typeof VULNERABILITY_FIELD_NAMES)[keyof typeof VULNERABILITY_FIELD_NAMES];

/**
 * Optimization: Add sourceOnly mode to skip unnecessary statistics queries in drawer context
 * Performance: Reduces 8 statistics queries to 1 when only source data is needed
 *
 * @param hookOptions.enabled - Enable/disable query execution
 * @param hookOptions.sourceOnly - When true, only fetch source statistics (drawer mode)
 */
export const useGetVulnerabilityFilters = (hookOptions?: {
  enabled?: boolean;
  originOnly?: boolean;
}) => {
  const { enabled = true, originOnly: sourceOnly = false } = hookOptions || {};

  // Get user type to determine available statuses
  const { isPraetorianUser } = useAuth();

  // Get vulnerability status mode for mode-aware labels
  const { mode } = useVulnerabilityStatusMode();

  // Fetch capabilities to determine integrations vs Chariot capabilities
  const {
    integrations,
    chariotCapabilities,
    status: capabilitiesStatus,
  } = useGetCapabilities();

  // Batch fetch all statistics in a single request
  // Performance: 90-97% faster than individual queries
  //
  // Optimization #1: sourceOnly mode for drawer context
  // Drawer only needs source statistics for column display
  // Main view needs source + tags statistics for filter dropdowns
  // Note: attackSurface, cvss, epss, kev, exploit stats removed - these use static options
  const batchStatistics = useStatisticsBatch({
    statisticKeys: sourceOnly
      ? [
          'risk#source#now', // Drawer: Only sources (needed for column display)
        ]
      : ['risk#source#now', 'risk#tags'], // Main view: source + tags statistics for filter dropdowns
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract individual statistics from batch response (memoized to avoid dependency issues)
  const sourceStats = useMemo(
    () => batchStatistics.data?.['risk#source#now'] || [],
    [batchStatistics.data]
  );
  const tagStats = useMemo(
    () => batchStatistics.data?.['risk#tags'] || [],
    [batchStatistics.data]
  );

  // Map priority (severity) options: use static list to ensure all are always available
  const severity = useMemo(() => {
    // Use static severity options to ensure all are always available
    // Use RiskSeverity letter codes as values (not numeric priorities) for backend compatibility
    return Object.values(RiskSeverity)
      .map(severity => ({
        value: severity, // Use letter code (C, H, M, L, I, E) for backend query
        label: SeverityDef[severity],
        id: severity,
        priority: RiskPriority[severity], // Add priority for sorting
      }))
      .sort((a, b) => a.priority - b.priority) // Sort by priority (ascending)
      .map(({ priority: _priority, ...item }) => item); // Remove priority from final result
  }, []);

  // Map status options: filtered by user type with mode-aware labels
  // Praetorian users see all statuses including Triaged
  // Customer users see all except Triaged
  const status = useMemo(() => {
    return getAvailableStatusOptions(isPraetorianUser, mode);
  }, [isPraetorianUser, mode]);

  const source = useMemo(() => {
    if (!sourceStats || capabilitiesStatus === 'pending') {
      return [];
    }

    const presentSources = (sourceStats || [])
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => String(stat.value));

    const chariotSources: DropdownOption[] = [];
    const integrationSources: DropdownOption[] = [];

    // Add special Chariot sources that aren't capabilities
    chariotSources.push(
      { label: 'Manual', value: '@praetorian.com', id: '@praetorian.com' },
      { label: 'Provided', value: 'provided', id: 'provided' },
      { label: 'Courier', value: 'burp-courier', id: 'burp-courier' },
      { label: 'Other', value: 'adhoc', id: 'adhoc' }
    );

    // Add Chariot capabilities that are present in the source stats
    chariotCapabilities.forEach(capability => {
      const capabilityName = capability.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(capabilityName)) {
        // Find the exact value from presentSources for this capability
        const exactValue =
          presentSources.find(s => s.toLowerCase() === capabilityName) ||
          capability.name;

        // Use frontend SHORT_NAME_OVERRIDES mapping utility (consistent with integrations)
        const displayLabel = getShortName(
          capability.title || capability.name,
          capability.name
        );

        chariotSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    // Process integrations from the integrations array
    integrations.forEach(integration => {
      const integrationName = integration.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(integrationName)) {
        // Find the exact value from presentSources for this integration
        const exactValue =
          presentSources.find(s => s.toLowerCase() === integrationName) ||
          integration.name;

        // Use frontend SHORT_NAME_OVERRIDES mapping utility
        const displayLabel = getShortName(
          integration.title || integration.name,
          integration.name
        );

        integrationSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    chariotSources.sort((a, b) => a.label.localeCompare(b.label));
    integrationSources.sort((a, b) => a.label.localeCompare(b.label));

    const result: (DropdownOption | GroupOption)[] = [];

    if (chariotSources.length > 0) {
      result.push({
        label: 'Praetorian',
        value: 'chariot',
        id: 'chariot',
        isGroup: true,
        items: chariotSources,
      });
    }

    result.push(...integrationSources);

    // Apply NSA/CISA consolidation transformation
    return transformSourceFilterOptions(result);
  }, [sourceStats, integrations, chariotCapabilities, capabilitiesStatus]);

  const tag = useMemo(() => {
    if (!tagStats) return [];
    return tagStats
      .filter(tag => tag.created === 'now')
      .map((statistic, index) => ({
        value: statistic.value,
        label: statistic.value,
        id: `${statistic.name}-${index}`,
        key: `${statistic.name}${statistic.value}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tagStats]);

  // Use static attack surface options (same as asset page) - attack surfaces are a
  // fixed platform taxonomy, not dynamic data. Always show all options.
  const attackSurface = useMemo(() => {
    return attackSurfaceOptions.map(option => ({
      id: option.id,
      label: option.label,
      value: option.value,
    }));
  }, []);

  // CVSS and EPSS use static range sliders (0-10 and 0-1) — no dynamic options needed
  // KEV is a boolean filter — uses static True/False options
  const cvss: DropdownOption[] = [];
  const epss: DropdownOption[] = [];
  const kev = BOOLEAN_FILTERS;

  // Compose the filter options object
  const options = useMemo(
    () => ({
      severityFilters: severity,
      statusFilters: status,
      sourceFilters: source,
      tagFilters: tag,
      attackSurfaceFilters: attackSurface,
      cvss,
      epss,
      kevFilters: kev,
    }),
    [severity, status, source, tag, attackSurface, cvss, epss, kev]
  );

  // Merge status - simplified with batch statistics
  // Note: priorityStatus and statusStatus removed since we use static options
  const mergeStatus = [batchStatistics.status, capabilitiesStatus].includes(
    'pending'
  )
    ? 'pending'
    : [batchStatistics.status, capabilitiesStatus].includes('error')
      ? 'error'
      : 'success';

  return { data: options, status: mergeStatus };
};
